import React, { useState } from 'react';
import styled from 'styled-components';
import { FormikValues } from 'formik';
import dayjs from 'dayjs';

import { Spacing } from '../../theme';
import { SelectElem } from '../../common/select';
import { ButtonElem } from '../../common/button';
import { COLOR_ENUM } from '../../theme/color';
import { FieldTextElem } from '../../common/field-text';
import { FieldTextAreaElem } from '../../common/field-text-area';
import { AlertActionElem } from '../../common/alert-action';
import { LoaderElem } from '../../common/loader';

import { LANGUAGE_LIST } from '../../lib/lang/value';
import { FORM_VALUE_ENUM } from './constant';
import { FieldDatePickerElem } from '../../common/field-date-picker';
import { ModalPopupTestView } from '../../data/popup/frame/modal-test-view';

export const Component: React.FC<{
  formik: FormikValues;
  getFieldError: Function;
  isSubmitDisabled: Function;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  errorMessage?: string;
  getFieldValue: Function;
  isFieldError: Function;
  setFieldValue: Function;
  handleButtonClick: Function;
  isOpen: boolean;
}> = ({
  isError,
  errorMessage,
  isSuccess,
  isLoading,
  isSubmitDisabled,
  formik,
  getFieldValue,
  getFieldError,
  isFieldError,
  setFieldValue,
  handleButtonClick,
  isOpen,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const [isTestModalOpen, setTestModalOpen] = React.useState(false);

  const onChange = (date: dayjs.Dayjs, _dateString: string | string[]) => {
    const dateValue = date?.format ? date.format('YYYY-MM-DD') : '';

    const today = dayjs().startOf('day');
    if (date.isBefore(today)) {
      setShowAlert(true);
      return;
    }
    setShowAlert(false);
    formik.setFieldValue(FORM_VALUE_ENUM.DEADLINE, dateValue);
  };

  const openTestPreview = () => {
    setTestModalOpen(true);
  };

  const POPUP_MODAL_TEST = {
    body: getFieldValue(FORM_VALUE_ENUM.BODY),
    linkUrl: getFieldValue(FORM_VALUE_ENUM.LINK_URL),
    imgUrl: getFieldValue(FORM_VALUE_ENUM.IMG_URL),
  };

  return (
    <>
      <Container>
        {!isOpen && (
          <ButtonElem
            type="submit"
            tid="EVENT.CREATE.CREATE_NAME"
            onClick={handleButtonClick}
          />
        )}
        {isOpen && (
          <ExpandContainer>
            <FieldTextAreaElem
              placeholder="POPUP.CREATE.BODY"
              name={FORM_VALUE_ENUM.BODY}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={getFieldValue(FORM_VALUE_ENUM.BODY)}
              errorMessage={getFieldError(FORM_VALUE_ENUM.BODY)}
              error={isFieldError(FORM_VALUE_ENUM.BODY)}
            />

            <FieldTextElem
              placeholder="POPUP.CREATE.IMG_URL"
              name={FORM_VALUE_ENUM.IMG_URL}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={getFieldValue(FORM_VALUE_ENUM.IMG_URL)}
              errorMessage={getFieldError(FORM_VALUE_ENUM.IMG_URL)}
              error={isFieldError(FORM_VALUE_ENUM.IMG_URL)}
            />
            <FieldTextElem
              placeholder="POPUP.CREATE.LINK_URL"
              name={FORM_VALUE_ENUM.LINK_URL}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={getFieldValue(FORM_VALUE_ENUM.LINK_URL)}
              errorMessage={getFieldError(FORM_VALUE_ENUM.LINK_URL)}
              error={isFieldError(FORM_VALUE_ENUM.LINK_URL)}
            />
            <DoubleContainer>
              <FieldDatePickerElem
                placeholder="POPUP.CREATE.DEADLINE"
                onChange={onChange}
                disabledDate={(current) =>
                  current && current.isBefore(dayjs(), 'day')
                }
              />
              <SelectElem
                dynamic
                placeholder="POPUP.CREATE.LANG"
                options={LANGUAGE_LIST}
                name={FORM_VALUE_ENUM.LANG}
                onChange={setFieldValue}
                errorMessage={getFieldError(FORM_VALUE_ENUM.LANG)}
                error={isFieldError(FORM_VALUE_ENUM.LANG)}
                defaultValue={getFieldValue(FORM_VALUE_ENUM.LANG)}
              />
            </DoubleContainer>

            <ButtonElem
              onClick={formik.handleSubmit}
              disabled={isSubmitDisabled()}
              type="primary"
              tid="POPUP.CREATE.SAVE_BUTTON"
            />

            <ButtonElem
              onClick={openTestPreview}
              type="submit"
              tid="POPUP.CREATE.TEST_VIEW"
            />
            {isTestModalOpen && (
              <ModalPopupTestView
                isOpen={isTestModalOpen}
                onClose={() => setTestModalOpen(false)}
                data={POPUP_MODAL_TEST}
              />
            )}
          </ExpandContainer>
        )}
      </Container>
      {isError && <AlertActionElem tid={`ERROR.${errorMessage}`} />}
      {isSuccess && <AlertActionElem type="success" tid="Success" />}
      {isLoading && <LoaderElem />}
    </>
  );
};

const DoubleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${Spacing(2)};
`;

const ExpandContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme[COLOR_ENUM.DIVIDER]};
  padding: ${Spacing(4)} 0 0 0;
  display: flex;
  flex-direction: column;
  gap: ${Spacing(3)};
`;

const Container = styled.div`
  display: grid;
  width: 100%;
  grid-gap: ${Spacing(3)};
`;
